import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import { type CSSProperties, useState } from 'react';
import { Icon } from './Icon.tsx';
import styles from './NaturalImage.module.css';

/** Shape of the placeholder when there is no image (or its shape is unknown). */
const FALLBACK_RATIO = 16 / 9;

export interface NaturalImageProps {
  readonly src: string | null;
  readonly alt: string;
  /** The image's width divided by its height. The frame takes exactly this shape before loading. */
  readonly ratio: number | null;
  /** Shown when there is no image or it fails to load. */
  readonly fallbackIcon: LucideIcon;
  readonly className?: string | undefined;
  readonly loading?: 'lazy' | 'eager' | undefined;
}

type LoadState = 'loading' | 'loaded' | 'failed';

/**
 * An image in its own proportions: never cropped, never letterboxed. The frame is exactly the
 * image. Limits come from the parent as `--image-max-width` / `--image-max-height`; when a limit
 * applies, the whole frame shrinks proportionally.
 */
const Frame = ({ src, alt, ratio, fallbackIcon, className, loading = 'lazy' }: NaturalImageProps) => {
  const [load, setLoad] = useState<LoadState>('loading');
  const shown = src !== null && load !== 'failed';
  const style = {
    '--image-ratio': String(shown && ratio !== null ? ratio : FALLBACK_RATIO),
  } as CSSProperties;
  return (
    <div
      className={clsx(styles.frame, shown && load === 'loading' && styles.pending, className)}
      style={style}
    >
      {shown ? (
        <img
          className={clsx(styles.image, load === 'loaded' && styles.visible)}
          src={src}
          alt={alt}
          loading={loading}
          decoding="async"
          onLoad={() => setLoad('loaded')}
          onError={() => setLoad('failed')}
        />
      ) : (
        <div className={styles.fallback} role="img" aria-label={alt}>
          <Icon icon={fallbackIcon} size="lg" />
        </div>
      )}
    </div>
  );
};

// A new source starts its own loading state.
export const NaturalImage = (props: NaturalImageProps) => <Frame key={props.src ?? 'none'} {...props} />;
